import {Component, ChangeDetectorRef} from "@angular/core";
import {EventBus} from "../event/EventBus";
import {ComponentBase} from "../base/ComponentBase";
import {UserModelService} from "../services/usermodel/usermodel.service";
import {CompletionState, StepStatus} from "../services/usermodel/usermodel.objects";
import {StepService} from "../services/step/step.service";
import {PointsChangedEvent} from "../event/StepEvents";
import {StepInitializedEvent} from "../event/StepEvents";
import {StopQuizEvent} from "../event/QuizEvents";
import {BookSessionType, Scilearn} from "../constants";
import {TaiEvent, TAI_STATUS} from "../event/TaiEvent";
import {ActivityModalEvent, ACTIVITY_STATUS} from "../event/ActivityEvent";
import {UserModelChangedEvent} from "../event/UserModelEvents";
import {ReportEventData} from "../event/ActivityEvent";
import {ModalConfig} from "../services/notification/modal.config";
import {StepStateUpdater} from "../services/step/step-state";
import {UserProgress} from "../services/usermodel/user-progress";
import {RaVersion} from "../services/configuration/ra.version";
import {PointService} from "../services/points/points.service";
import {AudioQueueItem, TranslateAudio} from "../services/audio-playback/audioqueue.item";
import {AudioQueueEvent, InterruptAudio} from "../event/AudioQueueEvent";
import {filter} from "rxjs/operators";

@Component({
    selector: "progression",
    templateUrl: "./progression.component.html",
    styleUrls: ["./progression.component.scss"]
})
export class ProgressionComponent extends ComponentBase {

    private star: boolean = false;
    private silverStar: boolean = false;
    private complete: boolean = false;
    private justCompletedSelection: boolean = false;
    private justCompletedRequiredRecordings: boolean = false;
    private justCompletedQuiz: boolean = false;
    private justCompletedTAI: boolean = false;
    private STEP_TRANSITION_TIME = 7000;

    constructor(private eventBus: EventBus,
                private userModelService: UserModelService,
                private stepService: StepService,
                private modalConfig: ModalConfig,
                private stepStateUpdater: StepStateUpdater,
                private userProgress: UserProgress,
                private pointService: PointService,
                private changeDetectorRef: ChangeDetectorRef) {
        super("ProgressionComponent");
        this.setProgressionState();
    }

    private setProgressionState(): void {
        let sub = this.eventBus.pipe(filter(event => event instanceof UserModelChangedEvent))
            .subscribe((event: UserModelChangedEvent) => {
                // these flags trigger the progression behaviors, but we have to wait for other things to finish first.
                console.log("progression: got user model event");
                this.justCompletedSelection = event.delta.justCompletedSelection;
                this.justCompletedRequiredRecordings = event.delta.justCompletedRequiredRecordings;
                this.justCompletedQuiz = event.delta.justCompletedQuiz;
                this.justCompletedTAI = event.delta.justCompletedTAI;
            });

        this.subscriptions.push(sub);

        sub = this.eventBus.pipe(filter(e => e instanceof TaiEvent)).subscribe((e: TaiEvent) => {
            if (e.status === TAI_STATUS.end_tai) {
                console.log("progression: got end tai event");
                this.updateStepOneProgressionUI();
            }
        });

        this.subscriptions.push(sub);

        sub = this.eventBus.pipe(filter(e => e instanceof ActivityModalEvent)).subscribe((e: ActivityModalEvent) => {
            if (e.data.type === Scilearn.FLUENCY_MODAL && e.data.automatic === true &&
                e.status === ACTIVITY_STATUS.modal_close) {
                // only update the step 2 progress UI after fluency was automatically opened
                // implication is that a reading was completed
                this.updateStepTwoProgressionUI();
            }
        });

        this.subscriptions.push(sub);

        sub = this.eventBus.pipe(filter(e => e instanceof StopQuizEvent)).subscribe((e: StopQuizEvent) => {
            this.updateStepThreeProgressionUI();
        });

        this.subscriptions.push(sub);
    }

    private timerReset(callback?: Function): void {
        setTimeout(() => {
            this.star = false;
            this.complete = false;
            this.silverStar = false;
            if (!!callback) {
                callback();
            }
        }, this.STEP_TRANSITION_TIME);
    }

    private showStarOrCompleteUI(showStar: boolean, showComplete: boolean, silverStar = false): void {
        this.star = showStar;
        this.complete = showComplete;
        this.silverStar = silverStar || false;
        this.stepService.setAutomaticMove(true);
        this.timerReset(() => {
            this.updateSelectionComplete();
        });
    }

    private updateStepOneProgressionUI(): void {
        let stepOneCompleted = this.stepStateUpdater.currentStepOneStatus === StepStatus.COMPLETION;
        let stepOneProficient = this.stepStateUpdater.currentStepOneStatus === StepStatus.PROFICIENCY;

        let showStar = this.userModelService.hasTAQStar && stepOneProficient;
        let showComplete = !showStar && stepOneCompleted;
        let pointsChange = this.pointService.currentPoints() !== this.userModelService.totalPoint;

        if ((showStar || showComplete) && (this.justCompletedTAI || pointsChange)) {
            this.justCompletedTAI = false;
            this.userProgress.setTransitionActive(true);
            this.playPointsAudio(() => {
                this.showStarOrCompleteUI(showStar, showComplete);

                if (this.star || this.complete) {
                    this.eventBus.next(new AudioQueueEvent(
                        [new AudioQueueItem(this.star ? "RA_STEP_1_STAR" : "RA_STEP_1_COMPLETED", TranslateAudio.YES, () => {
                            this.stepService.setStepTwoBlinking(true);
                            this.changeDetectorRef.detectChanges();
                        })], InterruptAudio.NO));
                }
            });
        }
        this.changeDetectorRef.detectChanges();
    }

    private updateStepTwoProgressionUI(): void {
        let stepTwoCompleted = this.stepStateUpdater.currentStepTwoStatus === StepStatus.COMPLETION;
        let stepTwoProficient = this.stepStateUpdater.currentStepTwoStatus === StepStatus.PROFICIENCY;
        let showStar = this.userModelService.hasRecordingStar && stepTwoProficient;
        let silverStar = this.userProgress.hasSilverRecordingStar();
        let showComplete = stepTwoCompleted && !showStar && !silverStar;
        if (this.justCompletedRequiredRecordings) {
            this.justCompletedRequiredRecordings = false;
            this.userProgress.setTransitionActive(true);
            this.playPointsAudio(() => {
                this.showStarOrCompleteUI(showStar, showComplete, silverStar);

                if (this.star || this.complete || this.silverStar) {
                    this.eventBus.next(new AudioQueueEvent(
                        [new AudioQueueItem(this.star ? "RA_STEP_GOLD_STAR" : "RA_STEP_2_COMPLETED", TranslateAudio.YES, () => {
                            this.stepService.setStepThreeBlinking(true);
                            this.changeDetectorRef.detectChanges();
                        })], InterruptAudio.NO));
                }
            });
        }
        this.changeDetectorRef.detectChanges();
    }

    private updateStepThreeProgressionUI(): void {
        let stepThreeCompleted = this.stepStateUpdater.currentStepThreeStatus === StepStatus.COMPLETION;
        let stepThreeProficient = this.stepStateUpdater.currentStepThreeStatus === StepStatus.PROFICIENCY;

        let showStar = this.userModelService.hasQuizStar && stepThreeProficient;
        let showComplete = !showStar && stepThreeCompleted;
        console.log("step3completed:" + stepThreeCompleted + " stepproficient:" + stepThreeProficient +
                    " showStar:" + showStar + " showComplete:" +
                    showComplete);
        if ((showStar || showComplete) && this.justCompletedQuiz) {
            this.justCompletedQuiz = false;
            this.userProgress.setTransitionActive(true);
            this.playPointsAudio(() => {
                this.showStarOrCompleteUI(showStar, showComplete);

                if (this.star) {
                    this.eventBus.next(new AudioQueueEvent(
                        [new AudioQueueItem("RA_STEP_GOLD_STAR", TranslateAudio.YES)], InterruptAudio.NO));
                } else if (this.complete) {
                    this.eventBus.next(new AudioQueueEvent(
                        [new AudioQueueItem("RA_STEP_3_COMPLETED", TranslateAudio.YES)], InterruptAudio.NO));
                }
                this.changeDetectorRef.detectChanges();
            });
        }
        this.changeDetectorRef.detectChanges();
    }

    private updateSelectionComplete(): void {
        let isAutomatic = true;
        let currentStepNotQuiz = this.stepService.current().getType() !== BookSessionType.QUIZ;
        console.log("story completion state: " + this.userModelService.storyCompletionState);

        if (this.justCompletedSelection) {
            this.justCompletedSelection = false;
            this.modalConfig
                .openModal(new ReportEventData(
                    {storyUniqName: null, type: Scilearn.SELECTION_MODAL, automatic: isAutomatic, storyLoaded: true}));
            this.stepService.setStepTwoBlinking(false);
            this.stepService.setStepThreeBlinking(false);
        } else if (currentStepNotQuiz && this.stepService.continueAutomaticMove()) {
            this.stepService.setStepTwoBlinking(false);
            this.stepService.setStepThreeBlinking(false);
            this.stepService.next();
        }

        this.userProgress.setTransitionActive(false);
    }

    private playPointsAudio(callback: Function): void {
        if (this.userModelService.totalPoint !== this.pointService.currentPoints()) {
            this.eventBus.next(new PointsChangedEvent());
            this.eventBus.next(new AudioQueueEvent([
                new AudioQueueItem("RA_POINTS_01", TranslateAudio.YES, () => { callback(); })
            ], InterruptAudio.NO));
        } else {
            callback();
        }
    }

    public getProgressionState(): any {
        return {
            "progression-gold-star": this.star,
            "progression-silver-star": this.silverStar,
            "progression-check-mark": this.complete,
            "active": this.star || this.silverStar || this.complete
        };
    }
}
